import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useProjectEndUsers } from '../../hooks/useProjectEndUsers';
import type { EndUser } from '../../types/endUser';
import {
  Badge,
  EmptyState,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../../ui';
import './EndUsersSettings.css';

interface EndUsersSettingsProps {
  projectId: string;
}

export function EndUsersSettings({ projectId }: EndUsersSettingsProps) {
  const { data: endUsers, isLoading, error } = useProjectEndUsers(projectId);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!endUsers) return [];
    const q = search.trim().toLowerCase();
    if (!q) return endUsers;
    return endUsers.filter(
      u => u.email.toLowerCase().includes(q) || (u.name ?? '').toLowerCase().includes(q)
    );
  }, [endUsers, search]);

  if (isLoading) return <div className="end-users-settings__loading">Loading end users…</div>;
  if (error) {
    return (
      <div className="end-users-settings__error">
        Failed to load end users: {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="end-users-settings">
      <div className="end-users-settings__header">
        <div>
          <h2>End Users</h2>
          <p className="end-users-settings__muted">
            People who have signed up to authenticate against this project.
          </p>
        </div>
        {endUsers && endUsers.length > 0 && (
          <Input
            type="search"
            leadingAddon={<Search size={14} aria-hidden="true" />}
            placeholder="Search by name or email"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search end users"
            wrapperClassName="end-users-settings__search"
          />
        )}
      </div>

      {endUsers && endUsers.length === 0 && (
        <EmptyState
          title="No end users yet."
          description="Once users sign up via your app, they'll appear here."
        />
      )}

      {endUsers && endUsers.length > 0 && (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Provider</TableHeaderCell>
                <TableHeaderCell>Signed up</TableHeaderCell>
                <TableHeaderCell>Last login</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(user => (
                <EndUserRow key={user.id} user={user} />
              ))}
            </TableBody>
          </Table>
          <p className="end-users-settings__count">
            {filtered.length === endUsers.length
              ? `${endUsers.length} user${endUsers.length === 1 ? '' : 's'}`
              : `${filtered.length} of ${endUsers.length} user${endUsers.length === 1 ? '' : 's'}`}
          </p>
        </>
      )}
    </div>
  );
}

function EndUserRow({ user }: { user: EndUser }) {
  return (
    <TableRow>
      <TableCell>
        <span className="end-users-settings__cell">
          {user.name ?? <span className="end-users-settings__placeholder">—</span>}
        </span>
      </TableCell>
      <TableCell>
        <span className="end-users-settings__email">{user.email}</span>
      </TableCell>
      <TableCell>
        {user.email_verified ? (
          <Badge tone="success">Verified</Badge>
        ) : (
          <Badge tone="warning">Unverified</Badge>
        )}
      </TableCell>
      <TableCell>
        <span className="end-users-settings__providers">
          {user.auth_methods && user.auth_methods.length > 0 ? (
            user.auth_methods.map(method => <ProviderBadge key={method} method={method} />)
          ) : (
            <span className="end-users-settings__placeholder">—</span>
          )}
        </span>
      </TableCell>
      <TableCell>
        <span className="end-users-settings__meta">{formatDate(user.created_at)}</span>
      </TableCell>
      <TableCell>
        <span className="end-users-settings__meta">
          {user.last_login_at ? formatDate(user.last_login_at) : '—'}
        </span>
      </TableCell>
    </TableRow>
  );
}

const PROVIDER_LABELS: Record<string, string> = {
  password: 'Password',
  magic_link: 'Magic link',
  google: 'Google',
  github: 'GitHub',
  microsoft: 'Microsoft',
  okta: 'Okta',
  apple: 'Apple',
};

function ProviderBadge({ method }: { method: string }) {
  const label = PROVIDER_LABELS[method] ?? method;
  return <Badge tone="neutral">{label}</Badge>;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
